import { useState, useEffect, useRef, useCallback } from "react";

// ─── PHYSICS CONSTANTS ────────────────────────────────────────────────────────
const PHYSICS = {
  R_earth: 6371e3,
  altitude: 450e3,
  get r_orbit() { return this.R_earth + this.altitude; },
  get T_orbit() { return 2 * Math.PI * Math.sqrt(this.r_orbit ** 3 / 3.986e14); },
  get groundTrackSpeed() { return 2 * Math.PI * this.R_earth / this.T_orbit; },
  get nadirShiftPerManeuver() { return 0.1 * (this.R_earth / this.r_orbit); }, // meters
  Isp: 65,       // s, cold gas N2
  g0: 9.81,
  impulseBit: 0.02, // Ns
  get propPerManeuver() { return this.impulseBit / (this.Isp * this.g0) * 1e6; }, // mg
};

// India bounding box and simplified SVG polygon (normalized 0-1 within bounds)
const INDIA_BOUNDS = { latMin: 8, latMax: 37, lonMin: 68, lonMax: 97 };
// Simplified India outline as [lon, lat] pairs
const INDIA_OUTLINE_RAW = [
  [68.1,23.6],[69.2,22.4],[70.5,22.0],[71.5,20.6],[72.6,20.3],[72.9,19.1],
  [73.1,17.8],[74.1,17.3],[74.3,16.4],[75.0,15.4],[76.3,14.6],[77.0,13.9],
  [77.7,13.1],[78.8,12.0],[79.9,10.3],[80.3,9.8],[80.2,8.9],[79.5,8.1],
  [78.1,8.3],[77.5,8.6],[76.7,8.4],[76.3,9.4],[76.4,10.5],[75.7,11.8],
  [74.9,12.9],[74.2,14.8],[73.8,15.9],[73.5,17.2],[72.8,18.6],[72.5,20.0],
  [71.8,21.1],[70.5,21.4],[69.6,22.1],[68.4,23.0],[68.5,24.3],[68.1,24.8],
  [68.8,25.4],[70.1,25.7],[70.9,25.1],[71.9,25.8],[72.5,26.4],[72.1,27.2],
  [72.4,28.4],[73.1,29.1],[73.9,29.9],[74.5,30.9],[74.8,31.6],[75.3,32.1],
  [75.9,32.6],[76.5,33.3],[77.1,33.9],[77.8,34.5],[78.7,34.9],[79.3,34.6],
  [79.5,33.2],[80.1,32.5],[81.0,31.1],[81.8,30.0],[82.0,29.1],[83.3,28.0],
  [84.2,27.5],[84.7,26.9],[85.3,26.6],[85.8,25.9],[86.5,25.2],[87.2,24.8],
  [88.0,24.5],[88.4,23.7],[88.7,22.8],[88.3,21.9],[87.5,21.2],[87.0,20.1],
  [86.7,19.2],[86.2,18.7],[85.3,18.3],[84.8,17.7],[84.1,17.0],[83.4,16.3],
  [82.3,15.2],[81.2,14.6],[80.3,13.9],[80.1,12.4],[80.7,11.4],[80.3,10.4],
  [79.4,9.6],[79.1,9.0],[79.8,8.4],[80.1,9.0],[80.6,10.0],[80.3,11.5],
  [80.8,12.7],[81.4,13.5],[82.1,14.4],[83.0,15.3],[83.8,16.1],[84.5,17.0],
  [85.0,17.8],[85.7,18.5],[86.3,19.1],[87.0,20.0],[87.4,21.0],[87.9,22.0],
  [89.0,22.5],[89.5,21.9],[90.3,21.8],[91.3,22.3],[91.9,23.4],[92.3,24.3],
  [93.3,24.1],[94.2,23.1],[94.9,23.7],[95.3,24.6],[95.1,25.9],[94.5,26.4],
  [93.7,26.7],[93.0,27.2],[92.0,27.8],[91.7,27.9],[91.2,28.1],[90.4,28.1],
  [89.6,28.2],[88.8,28.0],[88.0,27.9],[87.1,27.8],[86.3,28.0],[85.5,28.3],
  [84.6,28.6],[83.9,28.9],[83.1,29.6],[82.1,30.2],[80.5,31.0],[79.6,31.8],
  [78.7,32.6],[78.0,33.5],[77.2,34.1],[76.0,34.7],[74.9,35.5],[73.9,35.9],
  [73.0,35.7],[72.3,36.0],[71.4,36.0],[72.0,35.1],[72.5,34.5],[73.0,33.7],
  [73.8,33.2],[74.5,31.5],[74.1,30.9],[73.4,29.5],[72.5,28.0],[71.8,26.9],
  [71.0,25.8],[69.7,25.3],[69.0,24.5],[68.5,24.0],[68.1,23.6],
];

function lonLatToSVG(lon, lat, w, h) {
  const x = ((lon - INDIA_BOUNDS.lonMin) / (INDIA_BOUNDS.lonMax - INDIA_BOUNDS.lonMin)) * w;
  const y = (1 - (lat - INDIA_BOUNDS.latMin) / (INDIA_BOUNDS.latMax - INDIA_BOUNDS.latMin)) * h;
  return [x, y];
}

// Swath options
const SWATH_OPTIONS = [
  { label: "5 km (High Res)", value: 5000 },
  { label: "10 km (Medium)", value: 10000 },
  { label: "15 km (Wide)", value: 15000 },
];

export default function IndiaCoverageAnimation() {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const stateRef = useRef(null);

  const [swathKm, setSwathKm] = useState(5000);
  const [isRunning, setIsRunning] = useState(false);
  const [speed, setSpeed] = useState(3); // animation speed multiplier
  const [stats, setStats] = useState({
    currentPass: 0,
    totalPasses: 0,
    coveragePct: 0,
    propConsumed: 0,
    daysElapsed: 0,
    orbitShiftMm: 0,
    nadirShiftMm: 0,
    phase: "idle",
  });

  const T_orbit = PHYSICS.T_orbit;
  const nadirShift = PHYSICS.nadirShiftPerManeuver; // meters per maneuver
  const propPerPass = PHYSICS.propPerManeuver; // mg

  function computeTotalPasses(swath) {
    const indiaEW = (INDIA_BOUNDS.lonMax - INDIA_BOUNDS.lonMin) * 111320 * Math.cos(22.5 * Math.PI / 180);
    return Math.ceil(indiaEW / swath);
  }

  const totalPasses = computeTotalPasses(swathKm);

  // Draw India map with coverage bands
  const drawFrame = useCallback((ctx, W, H, passIndex, totalP, swath, animSatX, animSatY, thrustPhase) => {
    const PAD = { top: 60, left: 30, right: 220, bottom: 50 };
    const mapW = W - PAD.left - PAD.right;
    const mapH = H - PAD.top - PAD.bottom;

    // Background
    ctx.fillStyle = "#050d18";
    ctx.fillRect(0, 0, W, H);

    // Starfield
    ctx.save();
    for (let i = 0; i < 120; i++) {
      const sx = (i * 137.5) % W;
      const sy = (i * 97.3) % H;
      const alpha = 0.2 + (i % 5) * 0.12;
      ctx.fillStyle = `rgba(180,220,255,${alpha})`;
      ctx.fillRect(sx, sy, i % 3 === 0 ? 1.5 : 1, i % 3 === 0 ? 1.5 : 1);
    }
    ctx.restore();

    // Title
    ctx.fillStyle = "#00e5ff";
    ctx.font = "bold 13px 'Courier New', monospace";
    ctx.fillText("EP-ADCS ORBIT-SYNCHRONOUS STATION KEEPING", PAD.left, 22);
    ctx.fillStyle = "#557799";
    ctx.font = "10px 'Courier New', monospace";
    ctx.fillText(`100mm orbital shift → nadir step → India coverage tiling`, PAD.left, 38);

    // Map border
    ctx.strokeStyle = "#1a3a5c";
    ctx.lineWidth = 1;
    ctx.strokeRect(PAD.left, PAD.top, mapW, mapH);

    // Grid lines
    ctx.strokeStyle = "#0d2030";
    ctx.lineWidth = 0.5;
    for (let lon = 70; lon <= 96; lon += 5) {
      const [gx] = lonLatToSVG(lon, 20, mapW, mapH);
      ctx.beginPath(); ctx.moveTo(PAD.left + gx, PAD.top); ctx.lineTo(PAD.left + gx, PAD.top + mapH); ctx.stroke();
      ctx.fillStyle = "#1a3a5c"; ctx.font = "8px monospace";
      ctx.fillText(`${lon}°E`, PAD.left + gx - 8, PAD.top + mapH + 12);
    }
    for (let lat = 10; lat <= 35; lat += 5) {
      const [, gy] = lonLatToSVG(70, lat, mapW, mapH);
      ctx.beginPath(); ctx.moveTo(PAD.left, PAD.top + gy); ctx.lineTo(PAD.left + mapW, PAD.top + gy); ctx.stroke();
      ctx.fillStyle = "#1a3a5c"; ctx.font = "8px monospace";
      ctx.fillText(`${lat}°`, PAD.left - 25, PAD.top + gy + 3);
    }

    // Draw coverage swath bands (already covered)
    const swathDeg = swath / (111320 * Math.cos(22.5 * Math.PI / 180)) * (INDIA_BOUNDS.lonMax - INDIA_BOUNDS.lonMin) / (INDIA_BOUNDS.lonMax - INDIA_BOUNDS.lonMin);
    const swathPx = (swath / ((INDIA_BOUNDS.lonMax - INDIA_BOUNDS.lonMin) * 111320 * Math.cos(22.5 * Math.PI / 180))) * mapW;

    for (let p = 0; p < passIndex; p++) {
      const x = PAD.left + (p / totalP) * mapW;
      const hue = 160 + (p / totalP) * 40;
      const alpha = 0.18 + (p % 5 === 0 ? 0.08 : 0);
      ctx.fillStyle = `hsla(${hue}, 80%, 55%, ${alpha})`;
      ctx.fillRect(x, PAD.top, Math.max(swathPx, 1.5), mapH);
    }

    // Current active swath (glowing)
    if (passIndex < totalP) {
      const cx = PAD.left + (passIndex / totalP) * mapW;
      const gradient = ctx.createLinearGradient(cx, 0, cx + swathPx, 0);
      gradient.addColorStop(0, "rgba(0,229,255,0.0)");
      gradient.addColorStop(0.5, "rgba(0,229,255,0.35)");
      gradient.addColorStop(1, "rgba(0,229,255,0.0)");
      ctx.fillStyle = gradient;
      ctx.fillRect(cx, PAD.top, Math.max(swathPx * 2, 4), mapH);

      // Active nadir line
      ctx.strokeStyle = "#00e5ff";
      ctx.lineWidth = 1.5;
      ctx.setLineDash([4, 3]);
      ctx.beginPath();
      ctx.moveTo(cx + swathPx / 2, PAD.top);
      ctx.lineTo(cx + swathPx / 2, PAD.top + mapH);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // India outline
    ctx.beginPath();
    INDIA_OUTLINE_RAW.forEach(([lon, lat], i) => {
      const [x, y] = lonLatToSVG(lon, lat, mapW, mapH);
      if (i === 0) ctx.moveTo(PAD.left + x, PAD.top + y);
      else ctx.lineTo(PAD.left + x, PAD.top + y);
    });
    ctx.closePath();
    ctx.strokeStyle = "rgba(255,200,80,0.9)";
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // Satellite orbit arc (top strip above map)
    const orbitY = PAD.top - 28;
    ctx.strokeStyle = "#1a4060";
    ctx.lineWidth = 1;
    ctx.setLineDash([3, 4]);
    ctx.beginPath();
    ctx.moveTo(PAD.left, orbitY);
    ctx.lineTo(PAD.left + mapW, orbitY);
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.fillStyle = "#1a4060";
    ctx.font = "8px monospace";
    ctx.fillText("SSO 450km", PAD.left, orbitY - 5);

    // Satellite icon on orbit track
    const satX = PAD.left + animSatX * mapW;
    // Thruster plume
    if (thrustPhase !== 0) {
      const plumeDir = thrustPhase > 0 ? 1 : -1; // +1 = burn forward, -1 = retro
      const plumeGrad = ctx.createLinearGradient(satX, orbitY, satX + plumeDir * 18, orbitY);
      plumeGrad.addColorStop(0, "rgba(0,229,255,0.9)");
      plumeGrad.addColorStop(1, "rgba(0,229,255,0)");
      ctx.fillStyle = plumeGrad;
      ctx.beginPath();
      ctx.moveTo(satX, orbitY - 4);
      ctx.lineTo(satX + plumeDir * 20, orbitY);
      ctx.lineTo(satX, orbitY + 4);
      ctx.closePath();
      ctx.fill();
    }
    // Satellite body
    ctx.fillStyle = "#00e5ff";
    ctx.fillRect(satX - 6, orbitY - 4, 12, 8);
    ctx.strokeStyle = "#fff";
    ctx.lineWidth = 0.5;
    ctx.strokeRect(satX - 6, orbitY - 4, 12, 8);
    // Solar panels
    ctx.fillStyle = "#0a4060";
    ctx.strokeStyle = "#00e5ff";
    ctx.lineWidth = 0.5;
    ctx.fillRect(satX - 16, orbitY - 2, 8, 4);
    ctx.strokeRect(satX - 16, orbitY - 2, 8, 4);
    ctx.fillRect(satX + 8, orbitY - 2, 8, 4);
    ctx.strokeRect(satX + 8, orbitY - 2, 8, 4);

    // Nadir line from satellite down to map
    ctx.strokeStyle = "rgba(0,229,255,0.3)";
    ctx.lineWidth = 1;
    ctx.setLineDash([2, 3]);
    ctx.beginPath();
    ctx.moveTo(satX, orbitY + 4);
    ctx.lineTo(satX, PAD.top);
    ctx.stroke();
    ctx.setLineDash([]);

    // ── RIGHT PANEL ──────────────────────────────────────────────────────────
    const rx = W - PAD.right + 15;
    let ry = PAD.top + 10;

    function label(text, val, color = "#00e5ff", unit = "") {
      ctx.fillStyle = "#556677";
      ctx.font = "9px 'Courier New', monospace";
      ctx.fillText(text, rx, ry);
      ctx.fillStyle = color;
      ctx.font = "bold 13px 'Courier New', monospace";
      ctx.fillText(`${val}${unit}`, rx, ry + 14);
      ry += 34;
    }

    const coverage = Math.min(100, (passIndex / totalP) * 100);
    const days = (passIndex * T_orbit / 86400).toFixed(1);
    const propMg = (passIndex * propPerPass).toFixed(1);
    const propG = (passIndex * propPerPass / 1000).toFixed(3);

    label("PASS", `${passIndex} / ${totalP}`, "#00e5ff");
    label("COVERAGE", `${coverage.toFixed(1)}`, "#39ff14", "%");
    label("DAYS ELAPSED", days, "#ffcc00");
    label("PROPELLANT", `${propMg} mg`, "#ff6b35");
    label("", `(${propG} g total)`, "#aa4422");
    ry += 6;

    // Mini propellant bar
    ctx.fillStyle = "#0a1a2a";
    ctx.fillRect(rx, ry, 180, 10);
    ctx.fillStyle = "#ff6b35";
    ctx.fillRect(rx, ry, 180 * (passIndex / totalP), 10);
    ctx.strokeStyle = "#334455";
    ctx.lineWidth = 0.5;
    ctx.strokeRect(rx, ry, 180, 10);
    ry += 20;

    label("ORBIT SHIFT", "100 mm", "#aaddff");
    label("NADIR STEP", "93.4 mm", "#aaddff");
    label("SWATH WIDTH", `${(swath / 1000).toFixed(0)} km`, "#cc99ff");
    label("ALTITUDE", "450 km SSO", "#556677");

    // Coverage bar
    ry += 5;
    ctx.fillStyle = "#334455";
    ctx.font = "9px monospace";
    ctx.fillText("INDIA COVERAGE", rx, ry);
    ry += 12;
    ctx.fillStyle = "#0a1a2a";
    ctx.fillRect(rx, ry, 180, 14);
    const coverGrad = ctx.createLinearGradient(rx, 0, rx + 180, 0);
    coverGrad.addColorStop(0, "#0a5a2a");
    coverGrad.addColorStop(1, "#39ff14");
    ctx.fillStyle = coverGrad;
    ctx.fillRect(rx, ry, 180 * coverage / 100, 14);
    ctx.strokeStyle = "#334455";
    ctx.lineWidth = 0.5;
    ctx.strokeRect(rx, ry, 180, 14);
    ctx.fillStyle = "#ffffff";
    ctx.font = "bold 9px monospace";
    ctx.fillText(`${coverage.toFixed(1)}%`, rx + 180 * coverage / 100 + 3, ry + 10);
    ry += 30;

    // Phase indicator
    const phaseColors = { idle: "#334455", burn: "#00e5ff", coast: "#ffcc00", retro: "#ff6b35", complete: "#39ff14" };
    const phaseText = { idle: "IDLE", burn: "▶ BURN", coast: "— COAST", retro: "◀ RETRO", complete: "✓ COMPLETE" };
    ctx.fillStyle = "#334455";
    ctx.font = "9px monospace";
    ctx.fillText("MANEUVER PHASE", rx, ry);
    ry += 14;

    const phase = thrustPhase > 0 ? "burn" : thrustPhase < 0 ? "retro" : passIndex >= totalP ? "complete" : passIndex === 0 ? "idle" : "coast";
    ctx.fillStyle = phaseColors[phase] || "#334455";
    ctx.font = "bold 12px 'Courier New', monospace";
    ctx.fillText(phaseText[phase] || "IDLE", rx, ry);
  }, [totalPasses, swathKm, propPerPass, T_orbit]);

  const stopAnimation = useCallback(() => {
    if (animRef.current) cancelAnimationFrame(animRef.current);
    setIsRunning(false);
  }, []);

  const startAnimation = useCallback(() => {
    if (isRunning) { stopAnimation(); return; }
    setIsRunning(true);

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width;
    const H = canvas.height;
    const totalP = computeTotalPasses(swathKm);

    // Animation state
    const state = {
      passIndex: 0,
      satX: 0,         // 0 to 1 across map
      phase: "fly",    // fly | burn | retro | done
      phaseT: 0,
      thrustPhase: 0,
      lastTime: null,
    };
    stateRef.current = state;

    const FLY_DURATION = 800;    // ms to fly across map
    const BURN_DURATION = 350;   // ms for burn plume
    const RETRO_DURATION = 350;

    function tick(now) {
      if (!state.lastTime) state.lastTime = now;
      const dt = Math.min((now - state.lastTime) * speed, 80);
      state.lastTime = now;
      state.phaseT += dt;

      if (state.phase === "fly") {
        state.satX = Math.min(1, state.phaseT / FLY_DURATION);
        state.thrustPhase = 0;
        if (state.phaseT >= FLY_DURATION) {
          state.satX = 1;
          if (state.passIndex < totalP) {
            state.phase = "burn";
            state.phaseT = 0;
          } else {
            state.phase = "done";
          }
        }
      } else if (state.phase === "burn") {
        state.thrustPhase = 1;
        if (state.phaseT >= BURN_DURATION) {
          state.phase = "retro";
          state.phaseT = 0;
        }
      } else if (state.phase === "retro") {
        state.thrustPhase = -1;
        if (state.phaseT >= RETRO_DURATION) {
          state.passIndex++;
          state.phase = state.passIndex >= totalP ? "done" : "fly";
          state.satX = state.passIndex < totalP ? state.passIndex / totalP : 1;
          state.phaseT = 0;
          state.thrustPhase = 0;
          setStats(s => ({
            ...s,
            currentPass: state.passIndex,
            totalPasses: totalP,
            coveragePct: Math.min(100, (state.passIndex / totalP) * 100),
            propConsumed: state.passIndex * propPerPass,
            daysElapsed: (state.passIndex * T_orbit / 86400).toFixed(1),
          }));
        }
      }

      const targetX = state.passIndex / totalP;
      const animX = state.phase === "fly"
        ? targetX - (1 - state.satX) + state.satX * (1 / totalP)
        : targetX + (state.passIndex < totalP ? 0 : 0);

      const displayX = state.phase === "fly"
        ? (state.passIndex > 0 ? (state.passIndex - 1) / totalP : 0) + (state.satX / totalP)
        : (state.passIndex / totalP);

      drawFrame(ctx, W, H, state.passIndex, totalP, swathKm, displayX, 0, state.thrustPhase);

      if (state.phase !== "done") {
        animRef.current = requestAnimationFrame(tick);
      } else {
        drawFrame(ctx, W, H, totalP, totalP, swathKm, 1, 0, 0);
        setIsRunning(false);
        setStats(s => ({ ...s, currentPass: totalP, coveragePct: 100, phase: "complete" }));
      }
    }

    animRef.current = requestAnimationFrame(tick);
  }, [isRunning, swathKm, speed, drawFrame, stopAnimation, propPerPass, T_orbit]);

  // Initial draw
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    drawFrame(ctx, canvas.width, canvas.height, 0, computeTotalPasses(swathKm), swathKm, 0, 0, 0);
  }, [swathKm, drawFrame]);

  useEffect(() => () => { if (animRef.current) cancelAnimationFrame(animRef.current); }, []);

  const totalP = computeTotalPasses(swathKm);
  const totalDays = (totalP * T_orbit / 86400).toFixed(1);
  const totalPropG = (totalP * propPerPass / 1000).toFixed(2);

  return (
    <div style={{
      background: "#050d18",
      border: "1px solid #1a3a5c",
      borderRadius: "4px",
      padding: "16px",
      fontFamily: "'Courier New', monospace",
      color: "#aaccee",
      maxWidth: "1000px",
    }}>
      {/* Header */}
      <div style={{ marginBottom: "12px", borderBottom: "1px solid #1a3a5c", paddingBottom: "10px" }}>
        <div style={{ color: "#00e5ff", fontSize: "11px", letterSpacing: "2px", marginBottom: "3px" }}>
          ORBIT-SYNCHRONOUS STATION KEEPING ∷ INDIA COVERAGE PLANNER
        </div>
        <div style={{ color: "#556677", fontSize: "9px" }}>
          Each orbit: 100mm shift → 93.4mm nadir step → systematic tiling of subcontinent
        </div>
      </div>

      {/* Controls */}
      <div style={{ display: "flex", gap: "16px", marginBottom: "12px", flexWrap: "wrap", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: "8px", color: "#556677", marginBottom: "3px" }}>CAMERA SWATH</div>
          <select
            value={swathKm}
            onChange={e => { stopAnimation(); setSwathKm(Number(e.target.value)); }}
            style={{
              background: "#0a1a2a", border: "1px solid #1a3a5c", color: "#00e5ff",
              fontSize: "11px", padding: "4px 8px", fontFamily: "monospace", cursor: "pointer"
            }}
          >
            {SWATH_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        <div>
          <div style={{ fontSize: "8px", color: "#556677", marginBottom: "3px" }}>
            ANIMATION SPEED ×{speed}
          </div>
          <input type="range" min="1" max="8" value={speed}
            onChange={e => setSpeed(Number(e.target.value))}
            style={{ width: "100px", accentColor: "#00e5ff" }}
          />
        </div>

        <button
          onClick={startAnimation}
          style={{
            background: isRunning ? "#1a0a05" : "#003a50",
            border: `1px solid ${isRunning ? "#ff6b35" : "#00e5ff"}`,
            color: isRunning ? "#ff6b35" : "#00e5ff",
            padding: "6px 20px", fontSize: "11px", fontFamily: "monospace",
            cursor: "pointer", letterSpacing: "1px",
          }}
        >
          {isRunning ? "◼ STOP" : "▶ START MISSION"}
        </button>

        <button
          onClick={() => {
            stopAnimation();
            const canvas = canvasRef.current;
            if (canvas) {
              const ctx = canvas.getContext("2d");
              drawFrame(ctx, canvas.width, canvas.height, 0, totalP, swathKm, 0, 0, 0);
            }
            setStats({ currentPass: 0, totalPasses: totalP, coveragePct: 0, propConsumed: 0, daysElapsed: 0, phase: "idle" });
          }}
          style={{
            background: "#0a1a2a", border: "1px solid #334455", color: "#556677",
            padding: "6px 16px", fontSize: "11px", fontFamily: "monospace", cursor: "pointer",
          }}
        >
          ↺ RESET
        </button>
      </div>

      {/* Mission summary bar */}
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(5, 1fr)",
        gap: "8px", marginBottom: "12px",
        background: "#070f1a", border: "1px solid #112233", padding: "8px",
      }}>
        {[
          ["TOTAL PASSES", totalP],
          ["MISSION DAYS", totalDays],
          ["TOTAL ΔV", `${(totalP * 0.02 / 12 * 1000).toFixed(1)} mm/s`],
          ["PROPELLANT", `${totalPropG} g`],
          ["ORBIT PERIOD", `${(T_orbit / 60).toFixed(2)} min`],
        ].map(([lbl, val]) => (
          <div key={lbl} style={{ textAlign: "center" }}>
            <div style={{ fontSize: "7px", color: "#334455", letterSpacing: "1px" }}>{lbl}</div>
            <div style={{ fontSize: "12px", color: "#00e5ff", fontWeight: "bold" }}>{val}</div>
          </div>
        ))}
      </div>

      {/* Canvas */}
      <canvas
        ref={canvasRef}
        width={960}
        height={480}
        style={{ width: "100%", display: "block", border: "1px solid #112233" }}
      />

      {/* Bottom note */}
      <div style={{
        marginTop: "8px", fontSize: "8px", color: "#334455",
        borderTop: "1px solid #112233", paddingTop: "6px",
        display: "flex", gap: "20px", flexWrap: "wrap",
      }}>
        <span>▸ EMMS provides &lt;5μrad pointing → burn vector accuracy for each discrete impulse</span>
        <span>▸ Isp = 65s cold gas N₂ · 10mN thrust · 0.02Ns min impulse bit</span>
        <span>▸ 100mm orbital shift ≡ 93.4mm nadir displacement at ground</span>
        <span>▸ Orbit-sync SK fires once/orbit — not continuously against perturbations</span>
      </div>
    </div>
  );
}
